import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SITE_CONTAINER } from "@/lib/siteLayout";
import { CarCard } from "@/components/CarCard";
import api from "@/lib/api";
import {
  Building2,
  MapPin,
  Globe,
  Clock,
  Phone,
  Mail,
  Loader2,
  Car,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";

export default function SellerPublicProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get(`/sellers/${encodeURIComponent(slug)}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "A kereskedés nem található"))
      .finally(() => setLoading(false));
  }, [slug]);

  const seller = data?.seller;
  const cars = data?.cars || [];
  const live = data?.stats?.live_listings ?? cars.length;

  const locationLine = [seller?.city, seller?.county].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <main className={`${SITE_CONTAINER} py-10 md:py-14`}>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8"
          data-testid="dealer-back-home"
        >
          <ArrowLeft className="w-4 h-4" /> Vissza a kínálathoz
        </Link>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-electric" />
          </div>
        )}

        {!loading && error && (
          <div className="glass rounded-3xl p-10 text-center max-w-lg mx-auto" data-testid="dealer-not-found">
            <Building2 className="w-10 h-10 text-slate-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-light">{error}</h1>
            <p className="text-slate-400 text-sm mt-2">Lehet, hogy a profil rejtett vagy még nem kereskedői fiók.</p>
          </div>
        )}

        {!loading && seller && (
          <>
            <header
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-electric/15 via-white/[0.03] to-transparent p-8 md:p-10"
              data-testid="dealer-public-header"
            >
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-electric/10 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col md:flex-row md:items-start gap-6">
                <div className="shrink-0 w-16 h-16 rounded-2xl bg-electric/20 border border-electric/30 flex items-center justify-center">
                  {seller.picture ? (
                    <img src={seller.picture} alt="" className="w-full h-full rounded-2xl object-cover" />
                  ) : (
                    <Building2 className="w-8 h-8 text-electric" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-[0.2em] text-electric mb-1">Kereskedés</p>
                  <h1 className="font-heading text-3xl md:text-4xl font-light">{seller.display_name}</h1>
                  {seller.tagline && (
                    <p className="text-slate-300 mt-2 text-lg">{seller.tagline}</p>
                  )}
                  <div className="mt-5 flex flex-wrap gap-3">
                    <span
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm"
                      data-testid="dealer-live-count"
                    >
                      <Car className="w-4 h-4 text-electric" />
                      <span>
                        <b>{live}</b> aktív hirdetés
                      </span>
                    </span>
                    {locationLine && (
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300">
                        <MapPin className="w-4 h-4 text-electric" />
                        {locationLine}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {(seller.bio || seller.address || seller.opening_hours || seller.website || seller.phone || seller.email) && (
                <div className="relative mt-8 grid md:grid-cols-2 gap-6 pt-8 border-t border-white/10">
                  {seller.bio && (
                    <div className="md:col-span-2">
                      <h2 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Rólunk</h2>
                      <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{seller.bio}</p>
                    </div>
                  )}
                  <ul className="space-y-3 text-sm text-slate-300">
                    {seller.address && (
                      <li className="flex gap-2">
                        <MapPin className="w-4 h-4 text-electric shrink-0 mt-0.5" />
                        {seller.address}
                      </li>
                    )}
                    {seller.opening_hours && (
                      <li className="flex gap-2">
                        <Clock className="w-4 h-4 text-electric shrink-0 mt-0.5" />
                        {seller.opening_hours}
                      </li>
                    )}
                    {seller.website && (
                      <li className="flex gap-2">
                        <Globe className="w-4 h-4 text-electric shrink-0 mt-0.5" />
                        <a
                          href={seller.website.startsWith("http") ? seller.website : `https://${seller.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-electric hover:underline inline-flex items-center gap-1"
                        >
                          Weboldal <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                    )}
                    {seller.phone && (
                      <li className="flex gap-2">
                        <Phone className="w-4 h-4 text-electric shrink-0 mt-0.5" />
                        <a href={`tel:${seller.phone.replace(/\s/g, "")}`} className="hover:text-white">
                          {seller.phone}
                        </a>
                      </li>
                    )}
                    {seller.email && (
                      <li className="flex gap-2">
                        <Mail className="w-4 h-4 text-electric shrink-0 mt-0.5" />
                        <a href={`mailto:${seller.email}`} className="hover:text-white">
                          {seller.email}
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </header>

            <section className="mt-12">
              <h2 className="font-heading text-2xl font-light mb-6">Jelenlegi kínálat</h2>
              {cars.length === 0 ? (
                <p className="text-slate-400 text-sm" data-testid="dealer-empty-inventory">
                  Jelenleg nincs nyilvános autó a kínálatban — nézz vissza később.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dealer-inventory-grid">
                  {cars.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
